import {Box, Button} from "@mui/material";
import {ExitToApp as ExitIcon, VisibilityOutlined as EyeIcon} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../contexts/AuthContext";
import {RTL} from "../../cache/CacheRtl";

// A persistent indicator shown while a SYSTEM_ADMIN is impersonating a company user. Makes the
// borrowed identity obvious (so no one forgets they're acting as someone else) and offers a
// one-click exit back to the super-admin session.
//
// Mounted TWICE, deliberately:
//   - in TopNav, as the visible pill in the middle of the header bar;
//   - in App, with `selfHealOnly` — renders nothing, and exists purely so the orphaned-state
//     cleanup below still runs on pages that have no TopNav (the login screen), which is exactly
//     where that stale state needs clearing.
const ImpersonationBanner = ({selfHealOnly = false}) => {
  const {currentUser, isImpersonating, impersonatedName, stopImpersonating, clearImpersonation} = useAuth();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  // Impersonation only makes sense while someone is logged in. If we ever find the impersonation
  // keys present with NO current user, that's orphaned state — e.g. a logout that predated the
  // logout-clears-impersonation fix, or storage edited out-of-band. Self-heal: wipe the stale keys
  // so the indicator (and the parked super-admin token) can't survive a logged-out session, even in
  // a browser that already carries the bad state. This runs on mount/refresh, so no manual cleanup.
  const impersonating = !!isImpersonating && isImpersonating();
  useEffect(() => {
    if (impersonating && !currentUser && clearImpersonation) {
      clearImpersonation();
    }
  }, [impersonating, currentUser, clearImpersonation]);

  // Show only for a real, logged-in impersonation session.
  if (selfHealOnly || !impersonating || !currentUser) return null;

  const handleExit = async () => {
    setExiting(true);
    try {
      await stopImpersonating();
      // The super-admin may be on a company-only page (e.g. /app/clients) that they can no longer
      // access as a company-less SYSTEM_ADMIN — send them back to their dashboard.
      navigate("/app/dashboard");
    } finally {
      setExiting(false);
    }
  };

  return (
    <RTL>
      {/* An inline pill that sits in the middle of the top bar. It used to be a fixed full-width
          bar across the viewport bottom, which covered whatever the page rendered there (a grid's
          last row, a table's pagination) and made that content unreachable. In the header it's
          just as visible and overlaps nothing. */}
      <Box
        dir="rtl"
        data-testid="impersonation-banner"
        sx={{
          maxWidth: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 0.5,
          borderRadius: "999px",
          backgroundColor: "#7C3AED",
          color: "#fff",
          fontWeight: 700,
          fontSize: 12,
          minWidth: 0,
        }}
      >
        <EyeIcon sx={{fontSize: 16, flexShrink: 0}} />
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            // On a narrow screen the name matters more than the prefix — drop the label rather
            // than let the pill grow back across the viewport.
            display: {xs: "none", sm: "block"},
          }}
        >
          מצב התחזות — פועל כ{impersonatedName ? ` ${impersonatedName}` : "משתמש חברה"}
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<ExitIcon sx={{fontSize: 15}} />}
          disabled={exiting}
          onClick={handleExit}
          sx={{
            backgroundColor: "#fff",
            color: "#7C3AED",
            fontWeight: 700,
            fontSize: 11,
            textTransform: "none",
            borderRadius: "999px",
            px: 1.25,
            py: 0.25,
            minWidth: 0,
            whiteSpace: "nowrap",
            flexShrink: 0,
            "&:hover": {backgroundColor: "#F3E8FF"},
          }}
        >
          {exiting ? "יוצא..." : "יציאה"}
        </Button>
      </Box>
    </RTL>
  );
};

export default ImpersonationBanner;
